import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Typography
} from "@material-ui/core";

// Constants, utils
import { DATA_IDENTIFIER, ELEMENT } from "components/common/App/app-constant";
import { generateElementIdentifier } from "utils/ApplicationUtils";
import { selectPosTypes } from "../../config/redux/configurationsSlice";
import { downloadCSVFile, getServicePropositionObject, isObjectEmpty } from "../../utils/helperFunctions";
import { posListStyles } from "./POSListStyles";
import { CreatePosButton } from "./posUtils/PosAccessUtils";
import { a11yProps, POS_LIST_TABLE_COLUMNS, TabPanel } from "./posUtils/PosListViewUtils";
import { fetchPOSListByCountryId } from "./redux/posListSlice";
import { isArrayValid, isEmptyOrNull, isUndefined } from "../../utils/DataUtils";
import { downloadPOSDetailsReport, resetPOSDetails } from "../CreatePOS/redux/posSlice";
import { selectUserDetails } from "../Login/redux/userSlice";

import searchIcon from "../assets/svg/SearchIcon.svg";
import CustomAlert from "../common/CustomAlert/CustomAlert";
import Loader from "../common/Loader/Loader";
import MultiSelectBox from "../common/MultiSelectBox/MultiSelectBox";
import PrimaryButton from "../common/PrimaryButton/PrimaryButton";

//Common Components
import TextBox from "../common/TextBox";
import useCommonDataConfig from "../../config/redux/useCommonDataConfig";
import POSListTable from "./posUtils/POSListTable";

const POSList = () => {
  const classes = posListStyles();

  const [currentTab, setCurrentTab] = React.useState(0);
  const [posList, setPosList] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({ serviceType: [], posType: [] });
  const [searchText, setSearchText] = useState("");
  const [activePosList, setActivePosList] = useState([]);
  const [inactivePosList, setInactivePosList] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const dispatch = useDispatch();

  const { servicePropositionList } = useCommonDataConfig();
  const {
    currentCountry: { isoCode },
  } = useSelector((state) => state.appConfig);
  const posTypes = useSelector(selectPosTypes);
  const { data: responsePOSList, loading: fetchingPosList, error: posListError } = useSelector((state) => state.posList);
  const userInfo = useSelector(selectUserDetails);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    resetAllFilters();
  };

  const resetAllFilters = () => {
    setSearchText("");
    setAppliedFilters({ ...appliedFilters, serviceType: [], posType: [], });
  };

  // Define fetchPOSLists with useCallback
  const fetchPOSLists = useCallback(() => {
    dispatch(fetchPOSListByCountryId({ country: isoCode }));
  }, [dispatch, isoCode]);

  const includesIgnoreCase = useCallback((str, value) => str?.toLowerCase().includes(value.toLowerCase()), []);

  const filterByFieldIncludes = useCallback((data, field, values) => {
    return data.filter((row) => {
      const fieldValue = row[field];
      for (const val of values) {
        if (includesIgnoreCase(fieldValue, val)) {
          return true;
        }
      }
      return false;
    });
  }, [includesIgnoreCase]);

  const getFilteredList = React.useMemo(() => {
    const { posType, serviceType } = appliedFilters;
    const sourceList = currentTab === 0 ? activePosList : inactivePosList;

    // Guard against non-array or empty sourceList
    if (!isArrayValid(sourceList)) {
      return [];
    }

    let filteredList = [...sourceList];

    // Only apply filters if we have servicePropositionList
    if (isArrayValid(servicePropositionList) && serviceType?.length) {
      const serviceTypeNames = serviceType
        .map((id) => servicePropositionList.find((s) => s.id === id)?.name)
        .filter(Boolean);

      filteredList = filteredList.filter((row) =>
        row?.serviceTypes?.some((type) => serviceTypeNames.includes(type))
      );
    }

    // Filter by posType
    if (posType?.length) {
      filteredList = filterByFieldIncludes(filteredList, "posType", posType);
    }

    // Filter by search text
    if (!isEmptyOrNull(searchText)) {
      const loweredSearch = searchText.toLowerCase();
      filteredList = filteredList.filter(
        (row) =>
          row.posNo?.toLowerCase().includes(loweredSearch) ||
          row.storeName?.toLowerCase().includes(loweredSearch)
      );
    }

    // Remove duplicates based on unique POS number (assuming posNo is unique)
    const seen = new Set();

    return filteredList.filter((item) => {
      if (seen.has(item.posNo)) return false;
      seen.add(item.posNo);
      return true;
    });
  }, [activePosList, appliedFilters, currentTab, inactivePosList, searchText, servicePropositionList, filterByFieldIncludes]);

  const handlePosTypeFilter = (name, selectedValues) => {
    setAppliedFilters({ ...appliedFilters, [name]: selectedValues });
  };

  const handleServiceTypeFilter = (name, selectedValues) => {
    setAppliedFilters({ ...appliedFilters, [name]: selectedValues });
  };

  const handleSearch = (e) => {
    let { value } = e.target;
    setSearchText(value);
  };

  const handleExportCSVClick = async () => {
    setIsExporting(true);
    dispatch(downloadPOSDetailsReport(isoCode))
      .then(({ payload: { message: reportDownloadError, csvData, fileName } }) => {
        if (reportDownloadError) {
          CustomAlert("error", reportDownloadError);
        }

        if (csvData && fileName) {
          downloadCSVFile(fileName, csvData);
        }
      })
      .catch((error) => {
        console.error("Error downloading POS details report:", error);
        CustomAlert("error", error?.message ?? "Failed to download report.");
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const transformResponseList = (responsePOSList, servicePropositionList) => {
    if (!isArrayValid(responsePOSList) || !isArrayValid(servicePropositionList)) {
      return [];
    }

    return responsePOSList.map((response) => {
      if (!isUndefined(response) && isArrayValid(response.serviceTypeList)) {
        const updatedServiceTypeList = response.serviceTypeList.map((service) => {
          if (!isUndefined(service?.type) && !isEmptyOrNull(service.type)) {
            const details = getServicePropositionObject(servicePropositionList, service.type);
            if (!isObjectEmpty(details)) {
              return { ...service, displayName: details.displayName || "", rank: details.rank || 999 };
            }
          }
          return service;
        });
        updatedServiceTypeList.sort((a, b) => a.rank - b.rank);
        return { ...response, serviceTypeList: updatedServiceTypeList };
      }
      return response;
    });
  };

  // clear last selected POS details
  useEffect(() => {
    dispatch(resetPOSDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!isEmptyOrNull(responsePOSList) && servicePropositionList?.length > 0) {
      const recordList = transformResponseList(responsePOSList, servicePropositionList);
      setPosList(recordList);
      setActivePosList(recordList.filter((row) => row.active));
      setInactivePosList(recordList.filter((row) => row.active !== true || isUndefined(row?.active)));
    }
  }, [responsePOSList, servicePropositionList]);

  useEffect(() => {
    if (isoCode && servicePropositionList?.length > 0) {
      fetchPOSLists();
    }
  }, [isoCode, servicePropositionList, fetchPOSLists]);

  return (
    <div className={classes.posListSection}>
      <Grid container justifyContent="space-between" spacing={2}>
        <Grid item xs={6} className={classes.root}>
          <Typography className={classes.heading}>POS List</Typography>
          <div className={classes.posListCount}>{posList.length}</div>
        </Grid>
        <Grid item xs={6}>
          <Grid container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <PrimaryButton buttonLabel={isExporting ? "exporting..." : "Export CSV"} buttonVariant="outlined" disabled={isExporting} onClick={handleExportCSVClick} className={classes.fullWidthButton} />
            </Grid>
            <Grid item>
              <CreatePosButton classes={classes} userInfo={userInfo} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Card className={classes.cardSection}>
        <CardContent className={classes.tabsContainer}>
          <Tabs className={classes.tabs} value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
            <Tab label={`Active POS (${currentTab === 0 ? getFilteredList.length : activePosList.length})`} {...a11yProps(0)} className={classes.tabLabel} />
            <Tab label={`Inactive POS (${currentTab === 1 ? getFilteredList.length : inactivePosList.length})`} {...a11yProps(1)} className={classes.tabLabel} />
          </Tabs>
        </CardContent>
      </Card>
      <Grid container className={classes.searchAndFilterContainer}>
        <Grid item xs={3} className={classes.searchFilter}>
          <TextBox
            textBoxId="searchTextBox"
            placeholderText="Search by POS No. or Name"
            startAdornment={
              <InputAdornment position="start">
                <IconButton edge="start" size="small">
                  <img src={searchIcon} alt="search" />
                </IconButton>
              </InputAdornment>
            }
            value={searchText}
            handleChange={handleSearch}
          ></TextBox>
        </Grid>
        <Grid item xs={3}>
          {""}
        </Grid>
        <Grid item xs={6}>
          <Grid container alignItems="center">
            <Grid item xs={4} className={classes.filterByTextStyle}>
              <Typography className={classes.filterText}>Filter By</Typography>
            </Grid>
            <Grid item xs={4}>
              <MultiSelectBox data-testid={generateElementIdentifier({key: DATA_IDENTIFIER.POS_TYPE, element: ELEMENT.SELECT_DROPDOWN})} name={DATA_IDENTIFIER.POS_TYPE} label="POS Type" menuitems={posTypes} value={appliedFilters.posType} onChange={(e) => handlePosTypeFilter("posType", e)} className={classes.posTypeFilter} />
            </Grid>
            <Grid item xs={4}>
              <MultiSelectBox data-testid={generateElementIdentifier({key: DATA_IDENTIFIER.SERVICE_TYPE, element: ELEMENT.SELECT_DROPDOWN})} name={DATA_IDENTIFIER.SERVICE_TYPE} label="Service Type" menuitems={servicePropositionList} value={appliedFilters.serviceType} onChange={(e) => handleServiceTypeFilter("serviceType", e)} className={classes.serviceTypeFilter} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <TabPanel value={currentTab} index={0}>
        <div className={classes.tableStyle}>
          {fetchingPosList && <Loader />}
          {!fetchingPosList && isArrayValid(activePosList) && <POSListTable columnsData={POS_LIST_TABLE_COLUMNS} rowsData={getFilteredList} classes={classes} />}
          {!fetchingPosList && (!isArrayValid(activePosList) || activePosList.length === 0) && !posListError && <div data-testid="no-active-pos" className={classes.error}>No active POS data found</div>}
          {!fetchingPosList && posListError && <div className={classes.error}>{posListError.toString()}</div>}
        </div>
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        <div className={classes.tableStyle}>
          {fetchingPosList && <Loader />}
          {!fetchingPosList && isArrayValid(inactivePosList) && <POSListTable columnsData={POS_LIST_TABLE_COLUMNS} rowsData={getFilteredList} classes={classes} />}
          {!fetchingPosList && (!isArrayValid(inactivePosList) || inactivePosList.length === 0) && !posListError && <div className={classes.error}>No inactive POS data found</div>}
          {!fetchingPosList && posListError && <div className={classes.error}>{posListError.toString()}</div>}
        </div>
      </TabPanel>
    </div>
  );
};
export default POSList;
